import 'twin.macro';
import React, { Fragment, useEffect } from 'react';
import _groupBy from 'lodash-es/groupBy';
import { NotationSystem } from 'types/notationSystem';
import { Trans, useTranslation } from 'react-i18next';
import { useLivretEleveDiff } from 'containers/Livrets/queries/useLivretEleveDiff';
import { LivretEleve } from 'containers/Livrets/types/livret';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { SyncDiffTableRow } from './SyncDiffTableRow';
import { buildDiffKey } from './utils';
import Button from 'components/Buttons/Button';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useUpdateLivretEleveDiff } from 'containers/Livrets/queries/useUpdateLivretEleveDiff';
import Dialog from 'components/Dialog';
import { trackEvent } from 'queries/user/track';
import { Infobox } from 'components/Callout/Infobox';
import { SyncDiffTableRowBilan } from 'containers/Livrets/LivretEval/LivretEleve/components/LivretEleve/SyncLivret/SyncDiffTableRowBilan';
import { bold } from 'i18n/components';

interface Props {
  onClose: () => void;
  notationSystem: NotationSystem;
  livretEleve: LivretEleve;
  livretEvalId: string;
}

export function SyncLivretModalBody({
  onClose,
  livretEleve,
  notationSystem,
  livretEvalId,
}: Props) {
  const { t } = useTranslation(['carnet', 'common']);
  const [selectedChanges, setSelectedChanges] = React.useState<string[]>([]);
  const { data: diffs, isLoading } = useLivretEleveDiff({
    livretEleveId: livretEleve.id,
  });
  useEffect(() => {
    setSelectedChanges((diffs || [])?.map(buildDiffKey));
  }, [diffs]);
  const { mutateAsync: applyLivretEleveDiff, isLoading: isApplyingDiff } =
    useUpdateLivretEleveDiff({ livretEvalId });

  if (isLoading) {
    return (
      <Dialog.Body>
        <SpinnerComponent />
      </Dialog.Body>
    );
  }
  return (
    <>
      <Dialog.Body>
        <>
          <p>
            <Trans
              i18nKey="carnet:syncLivret.modal.description"
              components={{ bold }}
            />
          </p>
          {(diffs || []).length === 0 && (
            <Infobox tw="mt-4" intent="success">
              {t('carnet:syncLivret.modal.noChangeDetected')}
            </Infobox>
          )}
          {(diffs || []).length > 0 && (
            <>
              <div tw="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg my-8">
                <table tw="min-w-full divide-y divide-gray-300">
                  <tbody tw="divide-y divide-gray-200 bg-white">
                    {Object.entries(_groupBy(diffs, e => e.matiere)).map(
                      ([matiere, matiereDiffs]) => (
                        <Fragment key={matiere}>
                          <tr tw="bg-neutral-quiet">
                            <th colSpan={5} tw="py-2 px-2 text-center">
                              <span tw="font-normal uppercase text-sm">
                                {matiere}
                              </span>
                            </th>
                          </tr>
                          {matiereDiffs.map(d => {
                            const rowKey = buildDiffKey(d);
                            const onSelect = () => {
                              if (selectedChanges.includes(rowKey)) {
                                return setSelectedChanges(
                                  selectedChanges.filter(k => k !== rowKey),
                                );
                              }
                              return setSelectedChanges([
                                ...selectedChanges,
                                rowKey,
                              ]);
                            };
                            const isSelected = selectedChanges.includes(rowKey);

                            if (d.matiere === 'Bilan') {
                              return (
                                <SyncDiffTableRowBilan
                                  key={rowKey}
                                  diff={d}
                                  onSelect={onSelect}
                                  isSelected={isSelected}
                                />
                              );
                            }
                            return (
                              <SyncDiffTableRow
                                key={rowKey}
                                diff={d}
                                livretEleve={livretEleve}
                                onSelect={onSelect}
                                isSelected={isSelected}
                                notationSystem={notationSystem}
                              />
                            );
                          })}
                        </Fragment>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
              {(diffs || []).length > 0 && (
                <p tw="mt-5">{t('carnet:syncLivret.modal.hint')}</p>
              )}
            </>
          )}
        </>
      </Dialog.Body>
      <Dialog.Footer>
        <Button minimal large onClick={onClose}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          large
          intent="primary"
          loading={isApplyingDiff}
          disabled={selectedChanges.length === 0}
          onClick={() => {
            trackEvent('sync-livret : apply changes');
            applyLivretEleveDiff({
              livretEleveId: livretEleve.id,
              diff: (diffs || []).filter(d =>
                selectedChanges.includes(buildDiffKey(d)),
              ),
            }).then(() => {
              onClose();
            });
          }}
          icon={CheckIcon}
        >
          {t('carnet:syncLivret.modal.applyChanges')}
        </Button>
      </Dialog.Footer>
    </>
  );
}
